import { Component, OnDestroy, OnInit,  } from '@angular/core';
import { GroupAdminService } from '../services/groupadmin/groupadmin.service';
import { ReportGroup, GroupMember } from '../models/reportgroup';
import { Observable, Subscription } from 'rxjs';

import { EmployeeService } from '../services/employee/employee.service';
import { EmployeeListItem } from '../models/employeelistitem';
import { ReturnObject } from '../models/returnObject';
import { InternalMsgService } from '../services/internal.msg.service';
import { AccountService } from '../services/account.service';
import { AccountItem } from '../models/accountitem';
import { StatusMessagesService } from '../services/statusmessage.service';

@Component({
    selector: 'groupadmin',
    templateUrl: './groupadmin.component.html',
    styleUrls: ['./groupadmin.component.css']
})
export class GroupadminComponent implements OnInit, OnDestroy {
    msgSubscr: Subscription = new Subscription;
    reportGroupList: ReportGroup[] = [];
    employeeList: EmployeeListItem[] = [];
    filteredEmployees: Observable<EmployeeListItem[]> | undefined;
    selectedGroup: ReportGroup = new ReportGroup;
    loggedAccount: AccountItem = new AccountItem;
    spinnerMessage: string = 'Please wait';

    selectedEmployee = '';
    showDeleteGroupModal: boolean = false;
    showAddGroupModal: boolean = false;
    newgrouptext: string = '';

    constructor(private groupAdminService: GroupAdminService,
                private employeeService: EmployeeService,
                private msgService: InternalMsgService,
                private statusMessagesService: StatusMessagesService,
                private accountService: AccountService) {
    }

    // ----------------------------------------------------------------------------------
    // -- Life cycle hooks
    // ----------------------------------------------------------------------------------
    ngOnInit(): void {
        this.msgSubscr = this.msgService.loggedInAccountSet$.subscribe( 
            account => {
                this.loggedAccount = account;
                this.getData();
                this.getEmployeeList();
            } 
        );     
        this.accountService.fetchAccount();
    }

    ngOnDestroy(): void {
        this.msgSubscr.unsubscribe();
    }
    

    // ----------------------------------------------------------------------------------
    // -- HTTP --------------------------------------------------------------------------
    // ----------------------------------------------------------------------------------
    getData() : void {
        this.msgService.ontoggleSpinnerSet(true);
        this.groupAdminService.get(this.loggedAccount.email)
            .subscribe( { 
                next: (data: ReturnObject<ReportGroup[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    if (data.value.length > 0) {
                        this.reportGroupList = data.value;
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    saveData(group: ReportGroup): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.groupAdminService.save(group, this.loggedAccount.email, group.groupName )
            .subscribe( { 
                next: (data: ReturnObject<ReportGroup[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.reportGroupList = data.value;
                    this.statusMessagesService.showToasterSuccess('Saved', 'The group was saved. If its a new group, select it from the group list');    
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    deleteData(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.groupAdminService.delete( this.loggedAccount.email, this.selectedGroup.groupId )
            .subscribe( { 
                next: (data: ReturnObject<ReportGroup[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                      }
                    if (data.value.length > 0) {
                        this.reportGroupList = data.value;
                    } else {
                        const group = new ReportGroup();
                        group.member = [];
                        this.reportGroupList.push(group);
                    }
    
                    this.statusMessagesService.showToasterSuccess('Deleted', 'The group was deleted');
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getEmployeeList(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.employeeService.getEmployeeList()
            .subscribe( { 
                next: (data: ReturnObject<EmployeeListItem[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    
                    this.employeeList = data.value;
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }


    // ----------------------------------------------------------------------------------
    // -- DATA MANIPULATION 
    // ----------------------------------------------------------------------------------

    selectEmployee(employee: EmployeeListItem): void {
        this.selectedEmployee = `${employee.empName} - ${employee.email}`;
    }

    clearText(): void {
        if (this.selectedEmployee === '') { return; }
        this.selectedEmployee = '';
    }

    removeMember(member: GroupMember): void {
        if (!member) { return; }
        this.selectedGroup.member = this.selectedGroup.member.filter( x => x.email != member.email);
        this.saveData(this.selectedGroup);
    }

    addToGroupList(): void {
        if (this.selectedEmployee === '') { return; }
        
        const isAlreadySelected = this.selectedGroup.member.findIndex( x => x.email == this.selectedEmployee.split(' - ')[1]) != -1;
        if (isAlreadySelected) { return; }
        
        const newMember = new GroupMember();
        newMember.name = this.selectedEmployee.split(' - ')[0];
        newMember.email = this.selectedEmployee.split(' - ')[1];

        this.selectedGroup.member.push(newMember);
        this.selectedEmployee = '';
        this.saveData(this.selectedGroup);
    }

    // ----------------------------------------------------------------------------------
    // -- LOGIC
    // ----------------------------------------------------------------------------------
    groupListIdEmpty(): boolean {
        return this.reportGroupList.length == 0;
    }

    isGroupSelected(): boolean {
        return this.selectedGroup != null;
    }


    setSelectedGroup(group: ReportGroup): void {
        this.selectedGroup = group;
    }

    showDeleteDialog(): void {
        this.showDeleteGroupModal = true;
    }

    showAddDialog(): void {
        this.showAddGroupModal = true;
    }

    deleteGroup(): void {
        this.deleteData();
    }

    addGroup() {
        const newGroup = new ReportGroup();
        newGroup.groupName = this.newgrouptext;
        this.saveData(newGroup);
    }
}
